/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { NoteService } from './NoteService';
import { DriveService } from './DriveService';
import type { EditorService } from './EditorService';
import type { DBService } from './DBService';
import type { Note, NoteHistoryRecord } from '../types/types';
import type { AuthService } from './AuthService';
import type { DialogService } from './DialogService';

/**
 * Manages the "All Notes" and "Note History" side panes.
 */
export class PaneService {
    private statusManager: StatusManager;
    
    // Service registry getters
    private get dom() { return ServiceRegistry.get<DomController>('dom'); }
    private get dbService() { return ServiceRegistry.get<DBService>('dbService'); }
    private get driveService() { return ServiceRegistry.get<DriveService>('drive'); }
    private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
    private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
    private get authService() { return ServiceRegistry.get<AuthService>('authService'); }
    private get dialogService() { return ServiceRegistry.get<DialogService>('dialogService'); }

    private isNotesPaneOpen = false;
    private isHistoryPaneOpen = false;
    private _debouncedSearchHandler: () => void;

    constructor() {
        this.statusManager = new StatusManager(this.dom);
        this._debouncedSearchHandler = this._debounce(this.handleFetchLocalNotes.bind(this), 300);
        this.bindEventListeners();
    }

    private bindEventListeners(): void {
        this.dom.allNotesButton.addEventListener('click', () => this.toggleAllNotesView());
        this.dom.closeAllNotesPane.addEventListener('click', () => this.toggleAllNotesView());
        this.dom.allNotesOverlay.addEventListener('click', () => this.toggleAllNotesView());
        this.dom.fetchNotesButton.addEventListener('click', () => this.handleSyncWithDrive());
        this.dom.closeNoteViewer.addEventListener('click', () => this.closeNoteViewer());
        this.dom.historyButton.addEventListener('click', () => this.toggleNoteHistoryView());
        this.dom.closeNoteHistoryPane.addEventListener('click', () => this.toggleNoteHistoryView());
        this.dom.noteHistoryOverlay.addEventListener('click', () => this.toggleNoteHistoryView());
        this.dom.notesSortSelect.addEventListener('change', () => this.handleSortChange());
        this.dom.notesSearchInput.addEventListener('input', this._debouncedSearchHandler);
    }

    private _debounce(func: Function, delay: number) {
        let timeout: number;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = window.setTimeout(() => func.apply(this, args), delay);
        };
    }

    public async toggleAllNotesView(): Promise<void> {
        this.isNotesPaneOpen = !this.isNotesPaneOpen;
        this.dom.allNotesOverlay.classList.toggle('hidden', !this.isNotesPaneOpen);
        this.dom.allNotesPane.classList.toggle('open', this.isNotesPaneOpen);

        if (!this.isNotesPaneOpen) {
            this.closeNoteViewer();
        } else {
            this.dom.notesSearchInput.value = '';
            await this.handleFetchLocalNotes();
        }
    }

    public async handleFetchLocalNotes(): Promise<void> {
        this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">Fetching notes...</div>`;
        try {
            let notes = await this.dbService.getAllNotes();
            const sortBy = this.dom.notesSortSelect.value as 'modifiedTimestamp' | 'timestamp' | 'title';
            const searchTerm = this.dom.notesSearchInput.value.toLowerCase().trim();

            if (searchTerm) {
                notes = notes.filter(note => {
                    const category = note.category || '';
                    const tags = note.tags ? note.tags.join(' ') : '';
                    const content = `${note.title} ${note.rawTranscription} ${note.polishedNote} ${category} ${tags}`.toLowerCase();
                    return content.includes(searchTerm);
                });
            }

            notes.sort((a, b) => {
                if (sortBy === 'title') {
                    return a.title.localeCompare(b.title);
                }
                // For timestamps, descending order (newest first)
                return b[sortBy] - a[sortBy];
            });

            this.renderNoteList(notes);
        } catch (error) {
            ErrorHandler.handle('fetching notes from local database', error);
            this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">Error fetching notes from the local database.</div>`;
        }
    }

    private async handleSyncWithDrive(): Promise<void> {
        const syncAction = async () => {
            this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">🔍 Searching Google Drive for notes...</div>`;
            try {
                // Show progress during sync
                const remoteNotes = await this.driveService.listNotesFromDrive();
                this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">📥 Found ${remoteNotes.length} notes. Syncing metadata...</div>`;

                await this.driveService.syncNotesMetadata(this.dbService);

                this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">✅ Sync complete! Loading notes...</div>`;
                await this.handleFetchLocalNotes();
            } catch (error) {
                ErrorHandler.handle('syncing notes', error, this.dom, false);
                let errorMessage = '❌ Error syncing notes. Please try again.';

                if (error.message && error.message.includes('Failed to fetch notes')) {
                    errorMessage = '❌ Could not access Google Drive. Please check your permissions and try again.';
                } else if (error.message && error.message.includes('authentication')) {
                    errorMessage = '❌ Authentication issue. Please sign out and sign in again.';
                } else if (error.message && error.message.includes('folder')) {
                    errorMessage = '❌ Could not access ConFabulator folder. Please check if it exists in your Google Drive.';
                }

                this.dom.notesListContainer.innerHTML = `<div class="notes-list-message error">${errorMessage}<br><small>Check browser console for details</small></div>`;
            }
        };
        await this.authService.requestAuthAndRun(syncAction);
    }
    
    private renderNoteList(notes: Note[]): void {
        this.dom.notesListContainer.innerHTML = '';
        if (notes.length === 0) {
            const searchTerm = this.dom.notesSearchInput.value.trim();
            const message = searchTerm 
                ? `No notes found for "${searchTerm}".`
                : 'No local notes found. Try recording or syncing with Google Drive.';
            this.dom.notesListContainer.innerHTML = `<div class="notes-list-message">${message}</div>`;
            return;
        }
        notes.forEach(note => this.createNoteCard(note));
    }

    private createNoteCard(note: Note): void {
        const card = document.createElement('div');
        card.className = 'note-card';
        card.dataset.noteId = note.id;

        const driveIcon = note.driveFileId
            ? `<i class="fab fa-google-drive note-card-icon drive" title="Saved to Google Drive"></i>`
            : `<i class="fas fa-database note-card-icon local" title="Saved locally"></i>`;

        let tagsHtml = '';
        if (note.tags && note.tags.length > 0) {
            tagsHtml = `<div class="note-card-tags">${note.tags.map(tag => `<span class="note-card-tag">${tag}</span>`).join('')}</div>`;
        }

        let categoryHtml = '';
        if (note.category) {
            categoryHtml = `<div class="note-card-category">${note.category}</div>`;
        }

        const metadataHtml = (categoryHtml || tagsHtml) ? `<div class="note-card-metadata">${categoryHtml}${tagsHtml}</div>` : '';

        card.innerHTML = `
            <div class="note-card-main">
                <div class="note-card-header">
                    ${driveIcon}
                    <div class="note-card-title">${note.title}</div>
                </div>
                ${metadataHtml}
                <div class="note-card-dates">
                    <div class="note-card-date"><strong>Created:</strong> ${new Date(note.timestamp).toLocaleString()}</div>
                    <div class="note-card-date"><strong>Modified:</strong> ${new Date(note.modifiedTimestamp).toLocaleString()}</div>
                </div>
            </div>
            <div class="note-card-actions">
                <button class="note-card-button preview-button" title="Preview Note"><i class="fas fa-eye"></i></button>
                <button class="note-card-button edit-button" title="Load Note into Editor"><i class="fas fa-edit"></i></button>
                <button class="note-card-button delete-button" title="Delete Note"><i class="fas fa-trash-alt"></i></button>
            </div>
        `;

        card.querySelector('.preview-button')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleNotePreviewClick(note);
        });

        card.querySelector('.edit-button')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleEditNoteClick(note.id);
        });

        card.querySelector('.delete-button')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.dialogService.handleDeleteNoteClick(note);
        });

        this.dom.notesListContainer.appendChild(card);
    }
    
    private async handleNotePreviewClick(note: Note): Promise<void> {
        this.dom.notesListContainer.querySelectorAll('.note-card').forEach(card => {
            card.classList.toggle('active', (card as HTMLElement).dataset.noteId === note.id);
        });

        this.dom.noteViewerPanel.classList.remove('hidden');
        this.dom.noteViewerTitle.textContent = note.title;
        this.dom.noteViewerContent.innerHTML = `<div class="notes-list-message">Loading content...</div>`;

        try {
            let contentToShow = note.polishedNote || note.rawTranscription;

            // If this is a metadata-only note, load the full content first
            if (note._metadataOnly && note.driveFileId && this.driveService) {
                ErrorHandler.log('note preview', `Loading content for preview of metadata-only note: ${note.title}`);
                try {
                    const content = await this.driveService.loadNoteContent(note.driveFileId);
                    contentToShow = content.polishedNote || content.rawTranscription;

                    // Update the note object with the loaded content for future use
                    note.polishedNote = content.polishedNote;
                    note.rawTranscription = content.rawTranscription;
                    note.translatedNote = content.translatedNote;
                    note.timestamp = content.timestamp;
                    note._metadataOnly = false;

                    // Save the updated note locally
                    await this.dbService.saveNote(note);
                    ErrorHandler.log('note preview', `Content loaded for preview: ${note.title}`);
                } catch (loadError) {
                    ErrorHandler.handle('loading content for preview', loadError, this.dom, false);
                    contentToShow = 'Error loading note content. Please try editing the note instead.';
                }
            } else {
                ErrorHandler.log('note preview', `Previewing fully loaded note: ${note.title}`);
            }

            const htmlContent = await parseMarkdown(contentToShow || 'No content available');
            this.dom.noteViewerContent.innerHTML = htmlContent;
        } catch (error) {
            ErrorHandler.handle('loading note preview', error, this.dom, false);
            this.dom.noteViewerContent.innerHTML = `<div class="notes-list-message error">Error loading note content. Please try editing the note instead.</div>`;
        }
    }

    private async handleEditNoteClick(noteId: string): Promise<void> {
        this.toggleAllNotesView();
        await this.loadNoteIntoEditor(noteId);
    }
    
    public async loadNoteIntoEditor(noteId: string): Promise<void> {
        this.statusManager.loading('Loading note');
        try {
            await this.noteService.loadNote(noteId);
            this.editorService.resetHistory('polishedNote');
            this.editorService.resetHistory('translatedNote');
            this.updateHistoryButtonState();
            this.statusManager.custom(StatusMessages.NOTE_LOADED(this.noteService.currentNote?.title));

        } catch (error) {
            ErrorHandler.handle('loading note', error, this.dom);
        }
    }
    
    private closeNoteViewer(): void {
        this.dom.noteViewerPanel.classList.add('hidden');
        this.dom.notesListContainer.querySelectorAll('.note-card.active').forEach(card => {
            card.classList.remove('active');
        });
    }

    private handleSortChange(): void {
        this.handleFetchLocalNotes();
    }
    
    public updateHistoryButtonState(): void {
        this.dom.historyButton.disabled = !this.noteService.currentNote;
    }
    
    public async toggleNoteHistoryView(): Promise<void> {
        this.isHistoryPaneOpen = !this.isHistoryPaneOpen;
        this.dom.noteHistoryOverlay.classList.toggle('hidden', !this.isHistoryPaneOpen);
        this.dom.noteHistoryPane.classList.toggle('open', this.isHistoryPaneOpen);

        if (this.isHistoryPaneOpen) {
            await this._loadNoteHistory();
        }
    }

    private async _loadNoteHistory(): Promise<void> {
        const container = this.dom.historyContentContainer;
        container.innerHTML = `<div class="history-pane-message">Loading history...</div>`;

        if (!this.noteService.currentNote) {
            container.innerHTML = `<div class="history-pane-message">No active note selected.</div>`;
            return;
        }

        if (this.noteService.currentNote.driveFileId) {
            const revisions = await this.driveService.getNoteRevisions(this.noteService.currentNote.driveFileId);
            if (revisions.length === 0) {
                container.innerHTML = `<div class="history-pane-message">No previous versions found for this note.</div>`;
                return;
            }

            container.innerHTML = '';
            revisions.forEach(revision => {
                const card = this.createDriveRevisionCard(revision);
                container.appendChild(card);
            });
        } else {
            const history = await this.dbService.getNoteHistory(this.noteService.currentNote.id);
            if (history.length === 0) {
                container.innerHTML = `<div class="history-pane-message">No local history found for this note. Save the note to create a version.</div>`;
                return;
            }
            container.innerHTML = '';
            history.forEach(entry => {
                const card = this.createLocalRevisionCard(entry);
                container.appendChild(card);
            });
        }
    }
    
    private createDriveRevisionCard(revision: any): HTMLElement {
        const card = document.createElement('div');
        card.className = 'revision-card';
        card.addEventListener('click', () => this._viewDriveRevision(revision.id));

        const user = revision.lastModifyingUser;
        const userName = user?.displayName || 'Unknown User';
        const userPhoto = user?.photoLink || '';

        card.innerHTML = `
            <div class="revision-user">
                ${userPhoto ? `<img src="${userPhoto}" alt="${userName}" class="revision-user-avatar">` : ''}
                <span class="revision-user-name">${userName}</span>
            </div>
            <div class="revision-date">${new Date(revision.modifiedTime).toLocaleString()}</div>
        `;
        return card;
    }

    private createLocalRevisionCard(entry: NoteHistoryRecord): HTMLElement {
        const card = document.createElement('div');
        card.className = 'revision-card';
        card.addEventListener('click', () => this._viewLocalRevision(entry));

        card.innerHTML = `
            <div class="revision-user">
                <i class="fas fa-database" style="margin-right: 8px; color: var(--color-text-tertiary);"></i>
                <span class="revision-user-name">Local Save</span>
            </div>
            <div class="revision-date">${new Date(entry.snapshot.modifiedTimestamp).toLocaleString()}</div>
        `;
        return card;
    }

    private async _viewDriveRevision(revisionId: string): Promise<void> {
        if (!this.noteService.currentNote?.driveFileId) return;

        const container = this.dom.historyContentContainer;
        container.innerHTML = `<div class="history-pane-message">Loading revision...</div>`;

        try {
            const content = await this.driveService.getRevisionContent(this.noteService.currentNote.driveFileId, revisionId);
            // FIX: Call `parseNoteContent` as a static method on `DriveService`.
            const { title, rawTranscription, polishedNote, translatedNote } = DriveService.parseNoteContent(content);

            const htmlContent = await parseMarkdown(polishedNote || rawTranscription);

            container.innerHTML = `
                <div class="revision-viewer-header">
                    <button id="backToRevisions" class="back-to-revisions-button"><i class="fas fa-arrow-left"></i> Back</button>
                    <button id="restoreRevision" class="confirm-save-button">Restore this version</button>
                </div>
                <div class="revision-viewer-content">
                    <h3>${title}</h3>
                    <hr>
                    ${htmlContent}
                </div>
            `;

            container.querySelector('#backToRevisions')?.addEventListener('click', () => this._loadNoteHistory());
            container.querySelector('#restoreRevision')?.addEventListener('click', async () => {
                if (this.noteService.currentNote) {
                    this.noteService.currentNote.title = title;
                    this.noteService.currentNote.rawTranscription = rawTranscription;
                    this.noteService.currentNote.polishedNote = polishedNote;
                    this.noteService.currentNote.translatedNote = translatedNote;
                    await this.noteService.renderNoteUI();
                    this.editorService.resetHistory('polishedNote');
                    this.editorService.resetHistory('translatedNote');
                    await this.noteService.saveCurrentNote();
                    this.toggleNoteHistoryView();
                    this.statusManager.success('Note restored to previous version');
                }
            });
        } catch (error) {
            container.innerHTML = `<div class="history-pane-message">Error loading revision.</div>`;
            ErrorHandler.handle('loading revision', error, this.dom, false);
        }
    }

    private async _viewLocalRevision(entry: NoteHistoryRecord): Promise<void> {
        const container = this.dom.historyContentContainer;
        const { title, rawTranscription, polishedNote } = entry.snapshot;
        const htmlContent = await parseMarkdown(polishedNote || rawTranscription);

        container.innerHTML = `
            <div class="revision-viewer-header">
                <button id="backToRevisions" class="back-to-revisions-button"><i class="fas fa-arrow-left"></i> Back</button>
                <button id="restoreRevision" class="confirm-save-button">Restore this version</button>
            </div>
            <div class="revision-viewer-content">
                <h3>${title}</h3>
                <hr>
                ${htmlContent}
            </div>
        `;
        
        container.querySelector('#backToRevisions')?.addEventListener('click', () => this._loadNoteHistory());
        container.querySelector('#restoreRevision')?.addEventListener('click', async () => {
            if (this.noteService.currentNote) {
                const snapshot = entry.snapshot;
                this.noteService.currentNote.title = snapshot.title;
                this.noteService.currentNote.rawTranscription = snapshot.rawTranscription;
                this.noteService.currentNote.polishedNote = snapshot.polishedNote;
                this.noteService.currentNote.translatedNote = snapshot.translatedNote;
                this.noteService.currentNote.category = snapshot.category;
                this.noteService.currentNote.tags = snapshot.tags;

                await this.noteService.renderNoteUI();
                this.editorService.resetHistory('polishedNote');
                this.editorService.resetHistory('translatedNote');
                
                await this.noteService.saveCurrentNote(); 
                
                this.toggleNoteHistoryView();
                this.statusManager.success('Note restored to previous version');
            }
        });
    }
}